/**
 * WHY A REPORTED TICKET IS BEING CLOSED WITHOUT BEING FIXED.
 *
 * WHAT THIS IS STOPPING. Moving a card to Won't do emails the person who
 * reported it, and a bare "Closed" to somebody who did us the favour of
 * reporting a problem is the rudest message this system is capable of sending.
 * It reads as "we did not care", and it is usually not true — there is almost
 * always a real reason, and it takes one sentence.
 *
 * SKIPPABLE, AND THAT IS THE DESIGN. The rule is that a bare notice may not go
 * out, NOT that somebody must be made to justify themselves before they may
 * tidy a board. Choosing to send nothing is a worse outcome than a good
 * sentence and a better one than a cold notice, so it is offered plainly rather
 * than hidden behind the close button.
 *
 * THE CARD HAS ALREADY MOVED by the time this appears. Holding the board still
 * until somebody writes prose would make a routine tidy-up feel like a form,
 * and the thing being decided here is what the CUSTOMER is told — not where the
 * card sits.
 */
import { useRef, useState } from 'react';
import { useFocusTrap } from '../motion';
import { useApp } from '../store';

export default function WhyClosed() {
  const issueId = useApp((s) => s.tellingWhyClosed);
  const issue = useApp((s) => s.issues.find((i) => i.id === s.tellingWhyClosed));
  const reporter = useApp((s) => s.reporters[s.tellingWhyClosed] ?? '');
  const tellThem = useApp((s) => s.tellThemWhyClosed);
  const sayNothing = useApp((s) => s.sayNothingAboutClosing);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, !!issueId);

  if (!issueId) return null;

  const send = () => {
    if (!why.trim()) return;
    setBusy(true);
    setProblem('');
    tellThem(why.trim())
      .then(() => setWhy(''))
      .catch((e: Error) => setProblem(e.message))
      .finally(() => setBusy(false));
  };

  return (
    /* No click-away close: this asks a question, and dismissing it by missing
       the box is how somebody sends nothing without meaning to. */
    <div className="overlay setup-overlay" role="dialog" aria-modal="true" aria-label="Closing a reported ticket">
      <div ref={panelRef} tabIndex={-1} className="feedback why-closed">
        <h2>Closing {issue?.ref ?? 'this'} — what should they be told?</h2>
        <p className="hint">
          {reporter} reported this and is waiting to hear. One line is plenty — they get it by
          email, exactly as you write it.
        </p>
        <textarea
          className="input feedback-box"
          autoFocus
          placeholder="We are not able to change this one, because…"
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          disabled={busy}
        />
        {problem && (
          <p className="login-error" role="alert">
            Not sent — {problem}
          </p>
        )}
        <div className="feedback-foot why-closed-foot">
          {/*
            THE HONEST WORDING. "Cancel" would suggest the close is being undone
            and it is not — the card has already moved. What is being declined
            is telling them, so that is what the button says.
          */}
          <button className="btn" onClick={sayNothing} disabled={busy}>
            Say nothing
          </button>
          <button className="btn btn-primary" onClick={send} disabled={busy || !why.trim()}>
            {busy ? 'Sending…' : 'Send and close'}
          </button>
        </div>
      </div>
    </div>
  );
}
